/**
 * withTimeout — generic time-box helper for best-effort async work.
 *
 * Wrap a promise with a hard timeout. On timeout, rejects with a
 * tagged `TimeoutError` so callers can distinguish "Gemini hung" from
 * "Gemini errored". Use for any best-effort LLM / network call where
 * waiting longer than the budget is worse than skipping the result.
 *
 * SED Bootstrap Durability SD1: the silent 19-min stall at 0% on
 * 2026-05-14 lived in the unbounded Gemini sequences inside
 * `applyDocAwareConfidence` and `detectEvidenceConflicts`. Per-call
 * timeouts wrapped via this helper let a single hung Gemini call
 * skip a section instead of parking the whole bootstrap.
 *
 * Pure (no fetch, no globals). The timer is cleared whether the inner
 * promise resolves or rejects so we never leak a `setTimeout` handle.
 */

export class TimeoutError extends Error {
  /**
   * Brand flag — preferred over `instanceof TimeoutError` for narrowing
   * because Vitest's module isolation re-instantiates the class per
   * test module, breaking prototype-chain checks across boundaries.
   * Callers should use `isTimeoutError(e)` (defined below) instead.
   */
  readonly isTimeout = true;
  /** Short identifier passed by the caller — surfaces in the error message. */
  readonly label: string;
  /** Budget that was exceeded, in milliseconds. */
  readonly budgetMs: number;

  constructor(label: string, ms: number) {
    super(`[timeout] ${label} exceeded ${ms}ms budget`);
    this.name = 'TimeoutError';
    this.label = label;
    this.budgetMs = ms;
  }
}

/**
 * Type guard for TimeoutError. Use this rather than `instanceof` so
 * Vitest module isolation (which clones class identities across test
 * file boundaries) can't break the narrow.
 */
export function isTimeoutError(e: unknown): e is TimeoutError {
  if (e == null || typeof e !== 'object') return false;
  return (e as { isTimeout?: unknown }).isTimeout === true;
}

/**
 * Wrap a promise with a hard timeout.
 *
 * @param promise the work to time-box. Must already be running — `withTimeout`
 *                does not start work, it only races a timer against it.
 * @param ms      timeout budget in milliseconds. Must be ≥ 0.
 * @param label   short identifier surfaced in the error message — eg.
 *                `'doc-aware:climate'`. Helps the log reader localise
 *                which section / call hung when scanning console output.
 *
 * @returns the resolved value of the wrapped promise.
 * @throws  `TimeoutError` if the budget is exceeded before the promise
 *          settles; otherwise re-throws whatever the promise rejected with.
 */
export async function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race<T>([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new TimeoutError(label, ms)), ms);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
